import { Message, CreateMessage, MessageFeedback } from '@/models/message';
import { API_URL, fetcher, getAccessToken } from '@/config/api';

const DEFAULT_SEND_ERROR = 'Failed to send message. Please try again.';

const mapUploadHttpError = (status: number, bodyText: string): string => {
  const normalizedBody = (bodyText || '').trim();

  if (status === 413) {
    return 'Upload too large. Maximum file size is 20MB per file.';
  }

  if (normalizedBody) {
    try {
      const parsed = JSON.parse(normalizedBody);
      if (typeof parsed?.detail === 'string' && parsed.detail.trim()) {
        return parsed.detail.trim();
      }
      if (typeof parsed?.message === 'string' && parsed.message.trim()) {
        return parsed.message.trim();
      }
      if (typeof parsed?.error === 'string' && parsed.error.trim()) {
        return parsed.error.trim();
      }
      if (typeof parsed?.detail?.message === 'string' && parsed.detail.message.trim()) {
        return parsed.detail.message.trim();
      }
    } catch {
      // Body is plain text, use it as-is below.
    }
    return normalizedBody;
  }

  return DEFAULT_SEND_ERROR;
};

export async function listMessages(conversationId: string): Promise<Message[]> {
  return fetcher<Message[]>(`/api/conversations/${conversationId}/messages`);
}

export async function postMessage(
  conversationId: string, 
  data: CreateMessage & { files?: File[] }
): Promise<Message> {
  // Create FormData for handling files
  const form = new FormData();
  
  // Add text content and other message metadata
  form.append('text', data.text);
  if (data.model_key) form.append('model_key', data.model_key);
  if (data.agent_key) form.append('agent_key', data.agent_key);
  
  // Validate and add files
  if (data.files && data.files.length > 0) {
    // Validate file types and sizes
    const ALLOWED_EXTENSIONS = {
      documents: ['.pdf', '.docx', '.doc', '.txt'],
      images: ['.png', '.jpg', '.jpeg', '.gif', '.webp']
    };
    const ALL_ALLOWED = [...ALLOWED_EXTENSIONS.documents, ...ALLOWED_EXTENSIONS.images];
    
    const MAX_FILE_SIZE = {
      documents: 20 * 1024 * 1024,  // 20MB
      images: 20 * 1024 * 1024      // 20MB
    };
    const MAX_FILES = 5;
    const MAX_TOTAL_FILE_SIZE = 40 * 1024 * 1024; // 40MB
    const messageAttachmentLimitsText = `You reached a message attachment limit: up to ${MAX_FILES} files and up to ${(MAX_TOTAL_FILE_SIZE / (1024 * 1024)).toFixed(0)}MB total.`;
    
    // Check max number of files
    if (data.files.length > MAX_FILES) {
      throw new Error(messageAttachmentLimitsText);
    }

    const totalFileSize = data.files.reduce((total, file) => total + file.size, 0);
    if (totalFileSize > MAX_TOTAL_FILE_SIZE) {
      throw new Error(messageAttachmentLimitsText);
    }
    
    // Validate and add each file with the same field name
    for (let i = 0; i < data.files.length; i++) {
      const file = data.files[i];
      
      // Check file extension
      const fileName = file.name.toLowerCase();
      const fileExt = fileName.substring(fileName.lastIndexOf('.'));
      
      if (!ALL_ALLOWED.includes(fileExt)) {
        throw new Error(`File type not allowed: ${fileName}. Please use PDF, Word, text files, or images (PNG, JPG, GIF, WEBP).`);
      }
      
      // Determine if it's an image or document
      const isImage = ALLOWED_EXTENSIONS.images.includes(fileExt);
      const maxSize = isImage ? MAX_FILE_SIZE.images : MAX_FILE_SIZE.documents;
      const fileType = isImage ? 'image' : 'document';
      
      // Check file size
      if (file.size > maxSize) {
        const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(0);
        throw new Error(`File too large: ${fileName} (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum size for ${fileType}s is ${maxSizeMB}MB.`);
      }
      
      // Add file to form with the same field name for all files
      form.append('files', file);
    }
  }

  try {
    // Get MSAL Bearer token for Authorization header
    const token = await getAccessToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const res = await fetch(`${API_URL}/api/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: form,
      credentials: 'include',
      headers,
    });
    
    if (!res.ok) {
      const errorBody = await res.text();
      throw new Error(mapUploadHttpError(res.status, errorBody));
    }
    
    return await res.json();
  } catch (error) {
    // propagate the error to callers
    throw error;
  }
}

// Legacy method - kept for backwards compatibility
export async function postMessageForm(
  conversationId: string,
  text: string,
  model_key: string,
  agent_key: string,
  files: File[]
): Promise<Message> {
  return postMessage(conversationId, {
    text,
    model_key,
    agent_key,
    files
  });
}

// CoT (Chain of Thought) step data structure
export interface CotStep {
  type: 'cot_step';
  step_id: string;
  title: string;
  description?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'error';
}

// Hybrid streaming message function - supports both Server-Sent Events and JSON fallback
export async function postStreamingMessage(
  conversationId: string,
  data: CreateMessage & { files?: File[] },
  onToken: (token: string) => void,
  onComplete: () => void,
  onError: (error: Error) => void,
  onCotStep?: (step: CotStep) => void,
  onCitations?: (citations: any[]) => void,
  signal?: AbortSignal
): Promise<void> {
  try {
    // Create FormData for handling files
    const form = new FormData();
    
    // Add text content and other message metadata
    form.append('text', data.text);
    if (data.model_key) form.append('model_key', data.model_key);
    if (data.agent_key) form.append('agent_key', data.agent_key);
    if (data.thinking_mode) form.append('thinking_mode', data.thinking_mode);
    // Send the user's browser timezone (IANA name, e.g. "America/New_York") so the
    // backend can request calendar data in the correct local timezone from Graph API.
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    form.append('timezone', browserTimezone);
    
    // Add files if present
    if (data.files && data.files.length > 0) {
      for (const file of data.files) {
        form.append('files', file);
      }
    }
    
    // Get MSAL Bearer token for Authorization header
    const token = await getAccessToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Use the streaming endpoint
    const response = await fetch(`${API_URL}/api/conversations/${conversationId}/stream`, {
      method: 'POST',
      body: form,
      credentials: 'include',
      headers,
      signal,
    });
    
    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(mapUploadHttpError(response.status, errorBody));
    }
    
    // Check if the response is Server-Sent Events or JSON
    const contentType = response.headers.get('content-type') || '';
    
    if (contentType.includes('text/event-stream')) {
      // Handle Server-Sent Events streaming (new backend)
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      
      if (!reader) {
        throw new Error('No response body reader available');
      }
      
      let buffer = '';
      let completed = false;
      
      try {
        while (!completed) {
          const { done, value } = await reader.read();
          
          if (done) {
            break;
          }
          
          // Decode the chunk and add to buffer
          buffer += decoder.decode(value, { stream: true });
          
          // Process complete lines in the buffer
          const lines = buffer.split('\n');
          
          // Keep the last incomplete line in the buffer
          buffer = lines.pop() || '';
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const dataStr = line.substring(6).trim(); // Remove 'data: ' prefix and trim
                if (dataStr) {
                  const data = JSON.parse(dataStr);

                  if (data.type === 'cot_step' && onCotStep) {
                    // Handle Chain of Thought step event
                    onCotStep(data as CotStep);
                  } else if (data.type === 'token' && data.token) {
                    onToken(data.token);
                  } else if (data._citations && onCitations) {
                    // Handle citations event
                    onCitations(data._citations);
                  } else if (data.type === 'complete') {
                    onComplete();
                    completed = true;
                    break;
                  } else if (data.type === 'error') {
                    onError(new Error(data.error || 'Streaming error'));
                    completed = true;
                    break;
                  }
                }
              } catch (parseError) {
                // Ignore malformed JSON lines (no debug logging)
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } else {
      // Handle JSON response (legacy backend)
      const result = await response.json();
      
      if (result.success && result.text) {
        // Simulate streaming by sending the text word by word
        const words = result.text.split(' ');
        
        for (let i = 0; i < words.length; i++) {
          const token = i === 0 ? words[i] : ' ' + words[i];
          onToken(token);
          await new Promise(resolve => setTimeout(resolve, 50)); // Delay between tokens
        }
      }
      
      onComplete();
    }
    
  } catch (error) {
    // Check if the error is due to abort (user clicked stop)
    if (error instanceof Error && error.name === 'AbortError') {
      // Don't treat abort as an error - it's expected when user stops streaming
      onComplete();
    } else {
      onError(error as Error);
    }
  }
}

export async function updateMessageReaction(
  conversationId: string,
  messageId: string,
  reaction: 'like' | 'dislike' | null,
  feedback?: MessageFeedback
): Promise<Message> {
  return fetcher<Message>(`/api/conversations/${conversationId}/messages/${messageId}`, {
    method: 'PUT',
    body: JSON.stringify({
      reaction,
      ...(feedback ? { feedback } : {})
    }),
  });
}

/**
 * Save a partial assistant message when streaming is cancelled by the user.
 * This allows the partial response to be persisted for later retrieval.
 */
export async function savePartialMessage(
  conversationId: string,
  text: string,
  agentKey?: string,
  modelKey?: string,
  thinkingMode?: 'auto' | 'quick' | 'deep'
): Promise<Message> {
  return fetcher<Message>(`/api/conversations/${conversationId}/messages/partial`, {
    method: 'POST',
    body: JSON.stringify({
      text,
      agent_key: agentKey,
      model_key: modelKey,
      thinking_mode: thinkingMode,
      was_cancelled: true
    }),
  });
}